import { ItemPaging, ProgramGuide } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { Logger } from '@/utils';
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { AxiosError } from 'axios';
import type { ExposeInterface } from './types';

export const useFavoriteProgramsStore = defineStore('FavoritePrograms', (): ExposeInterface => {
    const { 
        emptyProgramGuide
    } = useEmptyLine();

    const { $services} = useNuxtApp();
    let _numberPage: number = 1;

    const _favoritePrograms: Ref<ProgramGuide | null> = ref(null);
    const getFavoritePrograms = computed((): ProgramGuide | null => _favoritePrograms.value);

    const fetchFavoritePrograms = async (): Promise<void> => {
        try {
            const response = await $services.favorites.getFavoritesLiveTv(1, AppConstants.ItemsInLine);
            _favoritePrograms.value = new ProgramGuide(response.items, null, response.paging);
        } catch (error) {
            _favoritePrograms.value = emptyProgramGuide();
            Logger.captureError(error as AxiosError);
        }
    };

    const fetchFavoriteProgramsNextPage = async (): Promise<void> => {
        try {
            _numberPage++;

            const response = await $services.favorites.getFavoritesLiveTv(_numberPage, AppConstants.ItemsInLine);
            if (!_favoritePrograms.value?.items || !response.items) {
                throw new Error('Favorite programs is empty or response is empty');
            }
            const items = [..._favoritePrograms.value.items, ...response.items];
            const paging = new ItemPaging(response.paging?.itemsOnPage, _numberPage, response.paging?.totalItems, response.paging?.totalPages);
            _favoritePrograms.value = new ProgramGuide(items, null, paging);
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    const _deletedIds: Ref<Set<string>> = ref(new Set());
    const getDeletedIdsPrograms: ComputedRef<Set<string>> = computed(() => _deletedIds.value);
    const saveDeletedIds = async (id: string): Promise<void> => {
        _deletedIds.value.add(id);
    };
    const clearDeletedIds = async (id: string): Promise<void> => {
        if(_deletedIds.value.has(id)) {
            _deletedIds.value.delete(id);
        }
    }
    const clearAllDeletedIds = (): void => {
        _deletedIds.value.clear();
    }

    const clearHard = (): void => {
        _favoritePrograms.value = null;
        _numberPage = 1;
    };

    const clearSoft = (): void => {
        _numberPage = 1;
        if (!_favoritePrograms.value?.items) {
            throw new Error('Favorite programs is empty');
        }
        const items = [..._favoritePrograms.value.items].slice(0, AppConstants.ItemsInLine);
        if (!_favoritePrograms.value.paging) {
            throw new Error('Favorite programs paging is empty');
        }
        const paging = new ItemPaging(_favoritePrograms.value.paging.itemsOnPage, _numberPage, _favoritePrograms.value.paging.totalItems, _favoritePrograms.value.paging.totalPages);
        _favoritePrograms.value = new ProgramGuide(items, null, paging);
    };


    return {
        getDeletedIdsPrograms,
        getFavoritePrograms,
        fetchFavoritePrograms,
        fetchFavoriteProgramsNextPage,
        saveDeletedIds,
        clearDeletedIds,
        clearAllDeletedIds,
        clearHard,
        clearSoft
    };
});